import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
const ease = [0.23, 1, 0.32, 1] as const;
const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23' // avoids "24:00:00" at midnight in some browsers
});
const dubaiTime = () => formatter.format(new Date());

// Every piece of the top line shares this exact box, so same size + same line box = same level
const LINE = 'h-[18px] leading-[18px]';
export function DubaiClock() {
  const [time, setTime] = useState(dubaiTime);

  // Tick exactly on each second boundary so digits never skip or double-update
  useEffect(() => {
    let id = 0;
    const tick = () => {
      setTime(dubaiTime());
      id = window.setTimeout(tick, 1000 - Date.now() % 1000 + 5);
    };
    id = window.setTimeout(tick, 1000 - Date.now() % 1000 + 5);
    return () => window.clearTimeout(id);
  }, []);
  return <div className="text-[13px]" data-id="element-61" data-mp="2cyio3utsxsjs">
      <p className={`flex items-center gap-1.5 whitespace-nowrap ${LINE}`} data-id="element-62" data-mp="2czk7qnovvnd4">
        <span className={`block font-medium tracking-[0.04em] text-fg/50 ${LINE}`} data-id="element-63" data-mp="27xuvx5tu75nz">DXB</span>
        <time dateTime={time} aria-label={`Dubai time ${time}`} className={`flex items-center font-medium tabular-nums text-fg ${LINE}`} data-id="element-64" data-mp="23wj5wz71wif9">
          {time.split('').map((char, i) => char === ':' ? <span key={`sep-${i}`} className={`block text-fg/40 ${LINE}`} aria-hidden="true" data-id="element-65" data-mp="2vqbfjkt835pe">
                :
              </span> : <Digit key={`slot-${i}`} value={char} data-id="element-66" data-mp="2mv57ef6hcbjp" />)}
        </time>
        <span className={`hidden text-fg/40 min-[400px]:block ${LINE}`} data-id="element-67" data-mp="2hxvgev3fzrtr">GMT+4</span>
      </p>
      <p className="mt-0.5 leading-[18px] tabular-nums text-fg/50" data-id="element-68" data-mp="3fcpuz3tnepvw">25.2048° N, 55.2708° E</p>
    </div>;
}
function Digit({
  value
}: {
  value: string;
}) {
  const reduce = useReducedMotion();
  // A fresh key on every change, so a digit returning to an earlier value never collides
  // with its own still-exiting copy
  const gen = useRef(0);
  const prev = useRef(value);
  if (prev.current !== value) {
    prev.current = value;
    gen.current += 1;
  }
  return (
    // Block-level slot with a fixed line box: clipping no longer shifts the baseline
    <span className={`relative block overflow-hidden ${LINE}`} aria-hidden="true" data-id="element-69" data-mp="3op6rnw1mq1dc">
      <span className={`invisible block ${LINE}`} data-id="element-70" data-mp="3aq74ytxofwjt">0</span>
      <AnimatePresence initial={false} data-id="element-71" data-mp="3fr4ob9ufawmw">
        <motion.span key={gen.current} initial={reduce ? {
          opacity: 0
        } : {
          y: '100%'
        }} animate={reduce ? {
          opacity: 1
        } : {
          y: '0%'
        }} exit={reduce ? {
          opacity: 0
        } : {
          y: '-100%'
        }} transition={{
          duration: 0.25,
          ease
        }} className={`absolute inset-0 block text-center ${LINE}`} data-id="element-72" data-mp="3frwiqhcaaobl">
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}